import { Formik, Form, Field } from 'formik'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import { CLOSE, SECONDARY } from 'app/constants/button'
import ResponsiveDialog from 'app/components/dialog/ResponsiveDialog'
import { setPasswordSchemaValidation } from 'commons/schemaValidation'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Icon,
} from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import { useState } from 'react'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import ContainedButton from 'app/componentV4/Button/ContainedButton'

const SetPasswordDialogFormik = ({
  isOpen,
  isPending,
  onCancel,
  onValidSubmit,
}) => {
  const { formatMessage } = useIntl()
  const [isShowNewPassword, setIsShowNewPassword] = useState(false)
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false)

  return (
    <ResponsiveDialog
      open={isOpen}
      isFullScreen={false}
      scroll="paper"
      maxWidth="xs"
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="text-blue-950">
            <FormattedMessage id="userMenu.setupPassword.label" />
          </div>
          <div>
            <IconButton
              aria-owns="close button"
              aria-haspopup="true"
              className="p-4"
              disabled={isPending}
              onClick={onCancel}
            >
              <Icon color="action" className="text-24 text-blue-950 font-600">
                close
              </Icon>
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      <Formik
        initialValues={{
          password: '',
          confirmPassword: '',
        }}
        validationSchema={setPasswordSchemaValidation(formatMessage)}
        onSubmit={(values, actions) => {
          onValidSubmit(values)
          actions.setSubmitting(false)
        }}
      >
        {({ values, touched, errors, isValid, isSubmitting }) => {
          const isEmptyForm = Object.values(values).every((v) => !v || v === '')
          return (
            <Form className="flex flex-col">
              <DialogContent>
                <div className="space-y-5">
                  <Field name="password">
                    {({ field }) => (
                      <LabelInput
                        label={formatMessage({
                          id: 'userMenu.enterPassword.label',
                        })}
                        required
                        className="w-full mb-0 text-blue-950"
                      >
                        <SimpleInput
                          {...field}
                          type={isShowNewPassword ? 'text' : 'password'}
                          id="new-password-input"
                          name="password"
                          placeholder={formatMessage({
                            id: 'userMenu.enterPassword.label',
                          })}
                          value={values.password}
                          size="medium"
                          fullWidth
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                tabIndex={-1}
                                aria-label="toggle new password visibility"
                                onClick={() =>
                                  setIsShowNewPassword(!isShowNewPassword)
                                }
                              >
                                {isShowNewPassword ? (
                                  <Visibility
                                    fontSize="small"
                                    className="text-grey-200"
                                  />
                                ) : (
                                  <VisibilityOff
                                    fontSize="small"
                                    className="text-grey-200"
                                  />
                                )}
                              </IconButton>
                            </InputAdornment>
                          }
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                        />
                      </LabelInput>
                    )}
                  </Field>
                  <Field name="confirmNewPassword">
                    {({ field }) => (
                      <LabelInput
                        label={formatMessage({
                          id: 'registerForm.confirmPasswordPlaceholder',
                        })}
                        required
                        className="w-full mb-0 text-blue-950"
                      >
                        <SimpleInput
                          {...field}
                          type={isShowConfirmPassword ? 'text' : 'password'}
                          id="confirm-password-input"
                          name="confirmPassword"
                          placeholder={formatMessage({
                            id: 'registerForm.confirmPasswordPlaceholder',
                          })}
                          value={values.confirmPassword}
                          size="medium"
                          fullWidth
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                tabIndex={-1}
                                aria-label="toggle new password visibility"
                                onClick={() =>
                                  setIsShowConfirmPassword(
                                    !isShowConfirmPassword,
                                  )
                                }
                              >
                                {isShowConfirmPassword ? (
                                  <Visibility
                                    fontSize="small"
                                    className="text-grey-200"
                                  />
                                ) : (
                                  <VisibilityOff
                                    fontSize="small"
                                    className="text-grey-200"
                                  />
                                )}
                              </IconButton>
                            </InputAdornment>
                          }
                          error={
                            touched.confirmPassword &&
                            Boolean(errors.confirmPassword)
                          }
                          helperText={
                            touched.confirmPassword && errors.confirmPassword
                          }
                        />
                      </LabelInput>
                    )}
                  </Field>
                </div>
              </DialogContent>

              <DialogActions className="mb-10">
                <div className="w-full flex justify-center space-x-5">
                  <OutlinedButtons
                    tabIndex={-1}
                    id="close-change-password-dialog"
                    type="button"
                    color={CLOSE}
                    disabled={isSubmitting || isPending}
                    onClick={onCancel}
                  >
                    <FormattedMessage id="text.close" />
                  </OutlinedButtons>
                  <ContainedButton
                    id="save-change-password-btn"
                    color={SECONDARY}
                    isLoading={isSubmitting || isPending}
                    type="submit"
                    disabled={
                      isSubmitting || !isValid || isPending || isEmptyForm
                    }
                  >
                    <FormattedMessage id="action.save" />
                  </ContainedButton>
                </div>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </ResponsiveDialog>
  )
}

export default SetPasswordDialogFormik
